import React, { useCallback, useEffect, useState } from "react";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
    useTheme,
} from "@mui/material";
import { SearchOutlined } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import Header from "../../components/Header";
import { tokens } from "../../theme";
import { listMokshaStaff, searchHotelStaff, setMokshaStaff } from "../../store/actions";
import StaffCard, { nameList } from "./StaffCard";

/**
 * Moksha access: who can see agent identity and guest contact details on Moksha bookings.
 *
 * The screen opens on the roster, because "who holds this?" is the question the flag
 * creates and nothing else in the platform can answer it. Search is how someone is added.
 *
 * Granting confirms; revoking applies at once but leaves the person on screen with the
 * button flipped back. Search is exact-match, so a revoked person who vanished could only
 * be recovered by retyping their whole address from memory - keeping the row is what makes
 * skipping the confirm safe.
 */
const PAGE_SIZE = 10;
const DIALOG_NAME_LIMIT = 5;

const MokshaStaff = () => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const colors = tokens(theme.palette.mode);

    const [email, setEmail] = useState("");
    const [search, setSearch] = useState(null); // { result, message, data }
    const [roster, setRoster] = useState([]);
    const [total, setTotal] = useState(0);
    const [unrecorded, setUnrecorded] = useState(0);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [confirming, setConfirming] = useState(null);
    const [busyId, setBusyId] = useState("");
    const [searching, setSearching] = useState(false);
    // Local rather than the global redux loading flag: that one is set by every request in
    // the app, so an unrelated one in flight would grey these controls out.
    const [paging, setPaging] = useState(false);
    // Kept on screen after a revoke so a misclick is one click to undo.
    const [revoked, setRevoked] = useState([]);

    const { isLoading } = useSelector(({ loading }) => ({ isLoading: loading }));

    const fetchRoster = useCallback(
        async (pageNumber = 1) => {
            const response = await dispatch(listMokshaStaff(pageNumber, PAGE_SIZE));
            if (response) {
                setRoster(response.data ?? []);
                setTotal(response.total ?? 0);
                setUnrecorded(response.unrecorded ?? 0);
                setHasMore(Boolean(response.hasMore));
                setPage(pageNumber);
            }
        },
        [dispatch]
    );

    useEffect(() => {
        fetchRoster(1);
    }, [fetchRoster]);

    const turnPage = async (pageNumber) => {
        setPaging(true);
        await fetchRoster(pageNumber);
        setPaging(false);
    };

    const handleSearch = async () => {
        const value = email.trim();
        if (!value) return;
        setSearching(true);
        try {
            const response = await dispatch(searchHotelStaff(value));
            // Cleared either way: leaving the previous person on screen after a failed search
            // invites acting on a result that belongs to a different email.
            setSearch(response ?? null);
        } finally {
            setSearching(false);
        }
    };

    const applyFlag = async (staff, grant) => {
        setBusyId(staff._id);
        let message;
        try {
            message = await dispatch(setMokshaStaff(staff._id, grant));
        } finally {
            setBusyId("");
        }
        if (!message) return;

        toast.success(message);

        if (grant) {
            setRevoked((prev) => prev.filter((person) => person._id !== staff._id));
        } else {
            setRevoked((prev) =>
                prev.some((person) => person._id === staff._id)
                    ? prev
                    : [{ ...staff, is_moksha_staff: false }, ...prev]
            );
        }

        // A grant puts the person at the top of the roster, so go to page 1. A revoke
        // removes them, which can empty the last page - step back rather than leave the
        // admin looking at nothing.
        const revokedLastOnPage = !grant && roster.length === 1 && page > 1;
        await fetchRoster(grant ? 1 : revokedLastOnPage ? page - 1 : page);

        // From the latest state, not the render that started the mutation - the search box
        // stays live, so a search that resolved meanwhile must not be overwritten.
        setSearch((prev) =>
            prev?.data?._id === staff._id
                ? { ...prev, data: { ...prev.data, is_moksha_staff: grant } }
                : prev
        );
    };

    const confirmGrant = async () => {
        const staff = confirming;
        setConfirming(null);
        await applyFlag(staff, true);
    };

    const actionFor = (staff) => {
        const busy = busyId === staff._id;
        if (staff.is_moksha_staff) {
            return (
                <Button
                    onClick={() => applyFlag(staff, false)}
                    disabled={busy}
                    variant="outlined"
                    sx={{ textTransform: "none", color: colors.redAccent[400], borderColor: colors.redAccent[700] }}
                >
                    {busy ? <CircularProgress color="inherit" size={18} /> : "Remove access"}
                </Button>
            );
        }
        return (
            <Button
                onClick={() => setConfirming(staff)}
                disabled={busy}
                variant="contained"
                color="secondary"
                sx={{ textTransform: "none" }}
            >
                {busy ? <CircularProgress color="inherit" size={18} /> : "Give Moksha access"}
            </Button>
        );
    };

    // Drawn below either as a roster row or under "Just removed", so the search block names
    // them instead of rendering a second card with its own button.
    const onRoster = (id) =>
        roster.some((person) => person._id === id) || revoked.some((person) => person._id === id);
    const confirmLive = (confirming?.properties ?? []).filter((p) => p?.link_active && p?.hotel_active);
    const confirmName =
        [confirming?.first_name, confirming?.last_name].filter(Boolean).join(" ") || confirming?.email;

    return (
        <Box m="20px" mb={15}>
            <Box m="20px 0">
                <Header
                    title="MOKSHA STAFF"
                    subtitle="Who can see agent and guest contact details on Moksha bookings"
                />
            </Box>

            {/* Find someone */}
            <Box sx={{ backgroundColor: colors.primary[400], borderRadius: 1, p: 2.5, mb: 3 }}>
                <Typography fontWeight="bold" color={colors.grey[100]} fontSize={14} mb={1.25}>
                    Find someone by email
                </Typography>
                <Box display="flex" gap={1} flexWrap="wrap">
                    <TextField
                        size="small"
                        variant="filled"
                        hiddenLabel
                        type="email"
                        placeholder="name@property.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                handleSearch();
                            }
                        }}
                        inputProps={{ "aria-label": "Email address to search for" }}
                        sx={{ flex: "1 1 320px" }}
                    />
                    <Button
                        onClick={handleSearch}
                        disabled={!email.trim() || searching}
                        variant="contained"
                        color="secondary"
                        startIcon={<SearchOutlined />}
                        sx={{ textTransform: "none", minWidth: 120 }}
                    >
                        {searching ? <CircularProgress color="inherit" size={18} /> : "Search"}
                    </Button>
                </Box>
                <Typography variant="body2" color={colors.grey[300]} fontSize={12.5} mt={1}>
                    Enter the whole address. Property owners cannot be given Moksha access.
                </Typography>
            </Box>

            {/* What the search found. Someone already on the roster below is named, not
                drawn a second time - two identical cards with two buttons is a trap. */}
            {search && (
                <Box mb={4}>
                    {search.result === "staff" && search.data ? (
                        onRoster(search.data._id) ? (
                            <Box
                                sx={{
                                    backgroundColor: colors.primary[400],
                                    borderRadius: 1,
                                    borderLeft: `4px solid ${colors.greenAccent[500]}`,
                                    p: 2.5,
                                }}
                            >
                                <Typography color={colors.grey[100]} fontSize={14}>
                                    {[search.data.first_name, search.data.last_name].filter(Boolean).join(" ") ||
                                        search.data.email}{" "}
                                    already has Moksha access.
                                </Typography>
                                <Typography color={colors.grey[300]} fontSize={13} mt={0.5}>
                                    They are in the list below.
                                </Typography>
                            </Box>
                        ) : (
                            <StaffCard staff={search.data} action={actionFor(search.data)} />
                        )
                    ) : (
                        <Box
                            sx={{
                                backgroundColor: colors.primary[400],
                                borderRadius: 1,
                                borderLeft: `4px solid ${colors.grey[700]}`,
                                p: 2.5,
                            }}
                        >
                            <Typography color={colors.grey[200]} fontSize={14}>
                                {search.message}
                            </Typography>
                        </Box>
                    )}
                </Box>
            )}

            {/* Just removed — still here so a misclick is one click back */}
            {revoked.length > 0 && (
                <Box mb={4}>
                    <Typography fontWeight="bold" color={colors.grey[100]} fontSize={15} mb={1.5}>
                        Just removed
                    </Typography>
                    {revoked.map((staff) => (
                        <StaffCard key={`revoked-${staff._id}`} staff={staff} action={actionFor(staff)} />
                    ))}
                </Box>
            )}

            {/* The roster */}
            <Box display="flex" alignItems="baseline" gap={1} mb={1.5}>
                <Typography fontWeight="bold" color={colors.grey[100]} fontSize={15}>
                    Has Moksha access
                </Typography>
                <Typography color={colors.grey[300]} fontSize={13}>
                    {total}
                </Typography>
            </Box>

            {unrecorded > 0 && (
                <Typography color={colors.grey[300]} fontSize={12.5} mb={1.5}>
                    {unrecorded} of these {unrecorded === 1 ? "was" : "were"} set directly in the database before
                    this screen existed, so there is no record of who granted {unrecorded === 1 ? "it" : "them"} or
                    when. Removing and re-adding someone records it.
                </Typography>
            )}

            {isLoading && !roster.length ? (
                <Box display="flex" justifyContent="center" mt={4}>
                    <CircularProgress />
                </Box>
            ) : roster.length ? (
                <>
                    {roster.map((staff) => (
                        <StaffCard key={staff._id} staff={staff} action={actionFor(staff)} />
                    ))}

                    {(page > 1 || hasMore) && (
                        <Box display="flex" gap={1} justifyContent="flex-end" mt={2}>
                            <Button
                                onClick={() => turnPage(page - 1)}
                                disabled={page <= 1 || paging}
                                sx={{ textTransform: "none", color: colors.grey[100] }}
                            >
                                Previous
                            </Button>
                            <Button
                                onClick={() => turnPage(page + 1)}
                                disabled={!hasMore || paging}
                                sx={{ textTransform: "none", color: colors.grey[100] }}
                            >
                                Next
                            </Button>
                        </Box>
                    )}
                </>
            ) : (
                <Box sx={{ backgroundColor: colors.primary[400], borderRadius: 1, p: 2.5 }}>
                    <Typography color={colors.grey[200]} fontSize={14}>
                        Nobody has Moksha access yet.
                    </Typography>
                    <Typography color={colors.grey[300]} fontSize={12.5} mt={0.5}>
                        Until someone is added, agent and guest contact details stay hidden on every Moksha
                        booking. Search for a person above to give them access.
                    </Typography>
                </Box>
            )}

            {/* Granting names the person and the properties it reaches */}
            <Dialog open={Boolean(confirming)} onClose={() => setConfirming(null)}>
                <DialogTitle sx={{ backgroundColor: colors.primary[400], color: colors.grey[100] }}>
                    Give Moksha access?
                </DialogTitle>
                <DialogContent sx={{ backgroundColor: colors.primary[400] }}>
                    <Typography color={colors.grey[200]} fontSize={14} mb={1.5} mt={1}>
                        <strong>{confirmName}</strong> will see the agent behind every Moksha booking, plus the
                        guest's email, phone and address.
                    </Typography>

                    {confirmLive.length > 0 ? (
                        <>
                            <Typography color={colors.grey[300]} fontSize={13.5}>
                                This applies at {confirmLive.length}{" "}
                                {confirmLive.length === 1 ? "property" : "properties"}:
                            </Typography>
                            <Typography color={colors.grey[100]} fontSize={13.5} mt={0.5}>
                                {nameList(confirmLive, DIALOG_NAME_LIMIT)}
                            </Typography>
                        </>
                    ) : (
                        <Typography color={colors.grey[300]} fontSize={13.5}>
                            They work at no active property, so this has no effect until they are given access to
                            one.
                        </Typography>
                    )}

                    {confirming && !confirming.is_active && (
                        <Typography color={colors.grey[300]} fontSize={13} mt={1.5}>
                            Their account is disabled, so they cannot log in until it is re-enabled.
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions sx={{ backgroundColor: colors.primary[400], px: 3, pb: 2 }}>
                    <Button onClick={() => setConfirming(null)} sx={{ textTransform: "none", color: colors.grey[300] }}>
                        Cancel
                    </Button>
                    <Button onClick={confirmGrant} variant="contained" color="secondary" sx={{ textTransform: "none" }}>
                        Give access
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default MokshaStaff;
